/**
 * One entity table within an integration section sub-tab, built on the standard
 * admin-list pieces so it matches the exception-setup / Regions lists and stays
 * DRY: `ReportFilterCard` (+ `useReportFilters`) for the filter card, and
 * `BaseDataTable` for sort + client pagination. Both URL-syncs are off so the
 * three sibling sub-tabs don't fight over the page's query string.
 *
 * The table uses the `enhanced` BaseDataTable variant - a toolbar header band over
 * teal column headers - with the shared `TableResultsToolbar` (Showing X-Y of N +
 * per-page + pager) mounted in the top band, matching our report tables.
 */
import React, { useEffect, useMemo, useRef } from 'react'

import BaseDataTable, {
  TableColumn,
} from '../../../../../components/shared/BaseDataTable'
import TableResultsToolbar from '../../../../../components/shared/TableResultsToolbar'
import ReportFilterCard from '../../../../../components/shared/filters/ReportFilterCard'
import { useReportFilters } from '../../../../../components/shared/filters/useReportFilters'
import type { FilterKey } from '../../../../../components/shared/filters/types'
import { useListPageState } from '../../../../../hooks/useListPageState'
import type { SectionEntityTable } from './integrationSection.types'

interface Props {
  entity: SectionEntityTable
  /** Page query string (owned by the section shell) for the entity's extra filter. */
  params: URLSearchParams
  setParams: (next: URLSearchParams) => void
}

const IntegrationEntityTable: React.FC<Props> = ({ entity, params, setParams }) => {
  // Filter card state (search + market) - the same shared card exception-setup
  // uses. Sort + pagination live in useListPageState. Neither syncs the URL; the
  // optional Category/Folder filter is URL-bound and owned by the shell.
  const enabledFilters = useMemo<FilterKey[]>(() => {
    const keys: FilterKey[] = []
    if (entity.searchText) keys.push('textSearch')
    if (entity.getMarket) keys.push('market')
    return keys
  }, [entity])

  const { filters, updateFilter, clearFilters, hasActiveFilters } =
    useReportFilters({ enabledFilters, syncToUrl: false })

  const { state, updateField, updateFields } = useListPageState({
    defaultSortField: entity.defaultSortField ?? entity.columns[0]?.key ?? 'id',
    defaultPerPage: 25,
    syncWithURL: false,
  })

  const extraFilter = entity.extraFilter
  const extraActive = extraFilter?.isActive(params) ?? false
  const paramsKey = params.toString()

  // Reset to page 1 whenever a filter changes (the hooks are independent).
  useEffect(() => {
    if (state.page !== 1) updateField('page', 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.textSearch, filters.market, paramsKey])

  // A market-scoped extra filter (the Category/Folder picker) selects from one
  // market's tree, so a market change strands the old selection (foreign id ->
  // empty table). Clear it when the market actually changes - the ref skips the
  // first render so a deep-linked filter (count-link) survives the initial mount.
  const prevMarket = useRef(filters.market)
  useEffect(() => {
    if (prevMarket.current !== filters.market) {
      prevMarket.current = filters.market
      if (extraFilter && extraActive) setParams(extraFilter.clear(params))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market])

  const handleClearFilters = () => {
    clearFilters()
    if (extraFilter && extraActive) setParams(extraFilter.clear(params))
  }

  const filtered = useMemo(() => {
    const needle = (filters.textSearch || '').trim().toLowerCase()
    const rows = entity.rows.filter((row) => {
      if (
        entity.getMarket &&
        filters.market &&
        entity.getMarket(row) !== filters.market
      ) {
        return false
      }
      if (extraFilter && !extraFilter.predicate(row, params)) {
        return false
      }
      if (needle && entity.searchText) {
        return entity.searchText(row).toLowerCase().includes(needle)
      }
      return true
    })
    const { sortField, sortDirection } = state
    return [...rows].sort((a, b) => {
      const aVal = (a as any)[sortField] ?? ''
      const bVal = (b as any)[sortField] ?? ''
      const cmp =
        typeof aVal === 'string' && typeof bVal === 'string'
          ? aVal.localeCompare(bVal)
          : aVal < bVal
            ? -1
            : aVal > bVal
              ? 1
              : 0
      return sortDirection === 'desc' ? -cmp : cmp
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, filters.textSearch, filters.market, paramsKey, state.sortField, state.sortDirection])

  const paged = useMemo(
    () =>
      filtered.slice((state.page - 1) * state.perPage, state.page * state.perPage),
    [filtered, state.page, state.perPage],
  )

  const columns: TableColumn[] = entity.columns.map((c) => ({
    key: c.key,
    label: c.label,
    align: c.align,
    sortable: c.sortable,
    render: c.render ? (row: any) => c.render!(row) : undefined,
  }))

  const noun = entity.label.toLowerCase()
  const emptyMessage =
    entity.rows.length === 0
      ? entity.emptyLabel ?? `No ${noun} yet - click Sync now to fetch.`
      : `No ${noun} match.`

  return (
    <>
      <ReportFilterCard
        filters={filters}
        enabledFilters={enabledFilters}
        onFilterChange={updateFilter}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters || extraActive}
        extraFilters={extraFilter?.render(params, setParams, { market: filters.market })}
      />
      <BaseDataTable
        variant="enhanced"
        title={entity.label}
        titleIcon={entity.icon}
        headerActions={
          <TableResultsToolbar
            page={state.page}
            perPage={state.perPage}
            total={filtered.length}
            onPageChange={(p) => updateField('page', p)}
            onPerPageChange={(n) => updateFields({ perPage: n, page: 1 })}
          />
        }
        columns={columns}
        data={paged}
        sortField={state.sortField}
        sortDirection={state.sortDirection}
        onSort={(field, direction) =>
          updateFields({ sortField: field, sortDirection: direction })
        }
        emptyMessage={emptyMessage}
        getRowKey={(row) => entity.rowKey(row)}
      />
    </>
  )
}

export default IntegrationEntityTable
